import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Link, Typography } from "@mui/material";
import { formatVendorDateTimeRome } from "../../../utils/vendorDateTime";
import useVendorAccessGate from "../../../hooks/useVendorAccessGate";
import ConfirmationModal from "../../Shared/confirmationModal";
import notificationsService from "../../../services/notificationsService";
import { VENDOR_PATHS } from "../../../config/vendorNavPaths";
import PageListEmptyState from "../../Shared/pageListEmptyState";
import CardNotifica from "./cardNotifica";
import DialogNotifica from "./dialogNotifica";
import { useNotificationUnread } from "../../../context/notificationUnreadContext";
import { dispatchAppError } from "../../../services/errors/errorDispatcher";
import { ValidationError } from "../../../services/errors/ValidationError";
import { useAuth } from "../../../services/providers/authProvider";

const BLOCK_SIZE = 10;

function formatNotifDate(raw) {
  return formatVendorDateTimeRome(raw);
}

function mapNotificationItem(raw) {
  const state = raw.state;
  const archived = state === "archived";
  const isRead = state === "read" || state === "archived";
  return {
    id: raw.id,
    title: raw.title,
    description: raw.content ?? "",
    date: formatNotifDate(raw.createdAt),
    isRead,
    archived,
  };
}

function mergeById(existing, batch) {
  const seen = new Set(existing.map((x) => String(x.id)));
  const out = [...existing];
  for (const row of batch) {
    const id = String(row.id);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(row);
  }
  return out;
}

function handleCaughtError(e, fallbackMessage) {
  if (e instanceof ValidationError || e?.name === "ValidationError") return;
  if (e?.response != null) return;
  dispatchAppError(e?.message || fallbackMessage);
}

const Notifiche = () => {
  useVendorAccessGate();
  const { refreshUnread } = useNotificationUnread();
  const { isImpersonated } = useAuth() || {};
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewQueryKey = VENDOR_PATHS.NOTIFICHE_VIEW_QUERY;
  const archivedValue = VENDOR_PATHS.NOTIFICHE_VIEW_ARCHIVED;

  const view = useMemo(() => {
    const raw = searchParams.get(viewQueryKey);
    if (raw === archivedValue) return "archived";
    return "active";
  }, [searchParams, viewQueryKey, archivedValue]);

  useEffect(() => {
    const raw = searchParams.get(viewQueryKey);
    if (raw != null && raw !== "" && raw !== archivedValue) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete(viewQueryKey);
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams, viewQueryKey, archivedValue]);

  const goToArchived = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(viewQueryKey, archivedValue);
      return next;
    });
  }, [setSearchParams, viewQueryKey, archivedValue]);

  const goToActiveList = useCallback(() => {
    if (view !== "archived") return;
    const idx = globalThis.history.state?.idx;
    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
      return;
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(viewQueryKey);
      return next;
    }, { replace: true });
  }, [navigate, view, setSearchParams, viewQueryKey]);

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(1);

  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [dialogNotif, setDialogNotif] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const listStateParam = view === "archived" ? "archived" : undefined;

  const loadInitial = useCallback(async () => {
    setIsInitialLoading(true);
    try {
      const res = await notificationsService.list({
        page: 0,
        size: BLOCK_SIZE,
        state: listStateParam,
      });
      const mapped = (res.items ?? []).map(mapNotificationItem);
      setItems(mapped);
      setTotalCount(res.totalCount ?? 0);
      setNextPage(1);
      refreshUnread();
    } catch (e) {
      console.error("notifications list:", e);
      setItems([]);
      setTotalCount(0);
      handleCaughtError(e, "Impossibile caricare le notifiche.");
    } finally {
      setIsInitialLoading(false);
    }
  }, [listStateParam, refreshUnread]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore) return;
    if (totalCount > 0 && items.length >= totalCount) return;

    setIsLoadingMore(true);
    try {
      const res = await notificationsService.list({
        page: nextPage,
        size: BLOCK_SIZE,
        state: listStateParam,
      });
      const batch = (res.items ?? []).map(mapNotificationItem);
      if (batch.length === 0) {
        setTotalCount(items.length);
        return;
      }
      const pageTotal = res.totalCount ?? totalCount;
      if (pageTotal > 0) {
        setTotalCount(pageTotal);
      }
      setItems((prev) => mergeById(prev, batch));
      setNextPage((p) => p + 1);
    } catch (e) {
      console.error("notifications load more:", e);
      handleCaughtError(e, "Impossibile caricare altri risultati.");
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, totalCount, items.length, nextPage, listStateParam]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const noMoreResults =
    totalCount > 0 ? items.length >= totalCount : items.length === 0 && !isInitialLoading;
  const loadMoreDisabled =
    isInitialLoading ||
    isLoadingMore ||
    noMoreResults ||
    (totalCount > 0 && items.length >= totalCount);

  const openDialog = useCallback(
    (n) => {
      if (isImpersonated) {
        setDialogNotif(n);
        return;
      }
      if (!n.archived && !n.isRead) {
        const updated = { ...n, isRead: true };
        setDialogNotif(updated);
        setItems((prev) =>
          prev.map((x) => (String(x.id) === String(n.id) ? { ...x, isRead: true } : x))
        );
        notificationsService
          .state({ notificationIds: [String(n.id)], state: "read" })
          .then(() => refreshUnread())
          .catch((e) => {
            handleCaughtError(e, "Impossibile aggiornare la notifica.");
          });
      } else {
        setDialogNotif(n);
      }
    },
    [isImpersonated, refreshUnread]
  );

  const handleToggleRead = useCallback(
    (id) => {
      if (isImpersonated) return;
      const target = items.find((x) => String(x.id) === String(id));
      if (!target) return;
      const willBeRead = !target.isRead;
      const nextState = willBeRead ? "read" : "not-read";
      setItems((prev) =>
        prev.map((x) =>
          String(x.id) === String(id) ? { ...x, isRead: willBeRead } : x
        )
      );
      setDialogNotif((cur) =>
        cur && String(cur.id) === String(id) ? { ...cur, isRead: willBeRead } : cur
      );
      notificationsService
        .state({ notificationIds: [String(id)], state: nextState })
        .then(() => refreshUnread())
        .catch((e) => {
          handleCaughtError(e, "Impossibile aggiornare lo stato di lettura.");
          loadInitial();
        });
    },
    [isImpersonated, items, refreshUnread, loadInitial]
  );

  const handleArchive = useCallback(
    (id) => {
      if (isImpersonated) return;
      setItems((prev) => prev.filter((x) => String(x.id) !== String(id)));
      setDialogNotif((cur) =>
        cur && String(cur.id) === String(id) ? null : cur
      );
      notificationsService
        .state({ notificationIds: [String(id)], state: "archived" })
        .then(() => refreshUnread())
        .catch((e) => {
          handleCaughtError(e, "Impossibile archiviare la notifica.");
          loadInitial();
        });
      setTotalCount((prev) => prev - 1);
    },
    [isImpersonated, refreshUnread, loadInitial]
  );

  const handleRestore = useCallback(
    (id) => {
      if (isImpersonated) return;
      setItems((prev) => prev.filter((x) => String(x.id) !== String(id)));
      setDialogNotif((cur) =>
        cur && String(cur.id) === String(id) ? null : cur
      );
      notificationsService
        .state({ notificationIds: [String(id)], state: "read" })
        .then(() => refreshUnread())
        .catch((e) => {
          handleCaughtError(e, "Impossibile ripristinare la notifica.");
          loadInitial();
        });
      setTotalCount((prev) => prev - 1);
    },
    [isImpersonated, refreshUnread, loadInitial]
  );

  const confirmDelete = useCallback(
    (id) => {
      if (isImpersonated) return;
      notificationsService
        .state({ notificationIds: [String(id)], state: "deleted" })
        .then(() => {
          setItems((prev) => prev.filter((x) => String(x.id) !== String(id)));
          setDialogNotif((cur) =>
            cur && String(cur.id) === String(id) ? null : cur
          );
          setDeleteId(null);
          refreshUnread();
        })
        .catch((e) => {
          handleCaughtError(e, "Impossibile eliminare la notifica.");
        });
      setTotalCount((prev) => prev - 1);
    },
    [isImpersonated, refreshUnread]
  );

  const variant = view === "archived" ? "archived" : "active";

  return (
    <>
      <Box display="flex" flexDirection="column" sx={{ width: "100%", position: "relative" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={1}
        >
          <Box minHeight={32}>
            {view === "archived" ? (
              <Link
                component="button"
                type="button"
                underline="none"
                onClick={goToActiveList}
                sx={{
                  fontFamily: "Rubik",
                  fontWeight: "bold",
                  color: "#0b3d87",
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  fontSize: 15,
                  p: 0,
                  transition: "font-weight 0.15s, color 0.15s",
                  "&:hover": {
                    fontWeight: 900,
                    color: "#e00036",
                  },
                }}
              >
                ⮜ Torna a notifiche
              </Link>
            ) : (
              <span />
            )}
          </Box>
          <Box>
            {view === "active" ? (
              <Link
                component="button"
                type="button"
                underline="none"
                onClick={goToArchived}
                sx={{
                  fontFamily: "Rubik",
                  fontWeight: "bold",
                  color: "#0b3d87",
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  fontSize: 15,
                  p: 0,
                  transition: "font-weight 0.15s, color 0.15s",
                  "&:hover": {
                    fontWeight: 900,
                    color: "#e00036",
                  },
                }}
              >
                Vedi archiviate ⮞
              </Link>
            ) : null}
          </Box>
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontFamily: "Rubik",
            fontWeight: "bold",
            color: "#0b3d87",
          }}
        >
          {view === "archived" ? "Notifiche archiviate" : "Notifiche"}
        </Typography>

        <Typography
          sx={{
            mt: 1,
            fontFamily: "Rubik",
            color: "#808080",
          }}
        >
          {view === "archived"
            ? "Consulta le notifiche che hai archiviato."
            : "Controlla le tue notifiche."}
        </Typography>

        <Box mt={3}>
          {isInitialLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <PageListEmptyState
              message={
                view === "archived"
                  ? "Non ci sono notifiche archiviate qui 🫤"
                  : "Non ci sono Notifiche qui 🫤"
              }
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <Box display="flex" flexDirection="column" gap={2}>
                {items.map((n) => (
                  <CardNotifica
                    key={n.id}
                    notification={n}
                    variant={variant}
                    onOpen={openDialog}
                    onToggleRead={handleToggleRead}
                    onArchive={handleArchive}
                    onDelete={(id) => setDeleteId(id)}
                    onRestore={handleRestore}
                    readOnly={isImpersonated}
                  />
                ))}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                  gap: 1,
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                {isLoadingMore ? (
                  <CircularProgress size={28} sx={{ color: "#0b3d87" }} />
                ) : null}
                <Button
                  variant="outlined"
                  disabled={loadMoreDisabled}
                  onClick={() => loadMore()}
                  sx={{
                    fontFamily: "Rubik",
                    textTransform: "none",
                    borderRadius: "999px",
                    px: 3,
                    fontWeight: "bold",
                    borderColor: "#0b3d87",
                    color: "#0b3d87",
                    "&:hover": {
                      borderColor: "#092b6a",
                      backgroundColor: "#e8f0ff",
                    },
                  }}
                >
                  {loadMoreDisabled && !isInitialLoading && items.length > 0
                    ? "Non ci sono altre notifiche"
                    : "Carica altro"}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <DialogNotifica
        open={Boolean(dialogNotif)}
        onClose={() => setDialogNotif(null)}
        notification={dialogNotif}
        variant={variant}
        onToggleRead={handleToggleRead}
        onArchive={handleArchive}
        onDelete={(id) => setDeleteId(id)}
        onRestore={handleRestore}
        readOnly={isImpersonated}
      />

      <ConfirmationModal
        open={deleteId != null}
        onClose={() => setDeleteId(null)}
        title="Eliminare questa notifica?"
        subtitle=""
        message="La notifica verra rimossa definitivamente dall'elenco."
        onConfirm={() => confirmDelete(deleteId)}
      />
    </>
  );
};

export default Notifiche;
